// components/common/Logo.tsx
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useState } from 'react';

interface LogoProps {
  className?: string;
  onClick?: () => void;
}

export const Logo = ({ className = '', onClick }: LogoProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const { scrollY } = useScroll();

  // Smooth scroll with spring physics
  const smoothScrollY = useSpring(scrollY, {
    stiffness: 30,
    damping: 15,
    mass: 0.5,
  });

  // Transform values
  const borderWidth = useTransform(smoothScrollY, [0, 300], [2, 6]);
  const translateY = useTransform(smoothScrollY, [0, 300], [0, -10]);

  return (
    <Link to="/">
      <motion.div
        className={`relative ${className}`}
        style={{ y: translateY }}
        whileHover={{ scale: 1.1 }}
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        onClick={onClick}
      >
        {/* Glow effect */}
        <motion.div
          className="absolute -inset-2 bg-gradient-to-r from-blue-400/20 to-blue-600/20 rounded-full blur-xl"
          initial={{ opacity: 0 }}
          animate={{ opacity: isHovered ? 1 : 0 }}
          transition={{ duration: 0.3 }}
        />

        {/* Profile Picture Container */}
        <motion.div
          className="relative h-12 w-12 rounded-full overflow-hidden"
          style={{ borderWidth }}
        >
          <motion.div
            className="w-full h-full rounded-full overflow-hidden"
            whileHover={{ scale: 1.1 }}
          >
            <img
              src={
                new URL('../../assets/images/IMG_0003.JPG', import.meta.url)
                  .href
              }
              alt="Profile"
              className="h-full w-full object-cover"
            />
          </motion.div>

          {/* Subtle border gradient - Option 1: Blue theme */}
          <motion.div
            className="absolute inset-0 rounded-full"
            animate={{
              background: [
                'linear-gradient(0deg, rgba(59,130,246,0.2) 0%, rgba(37,99,235,0.2) 100%)',
                'linear-gradient(360deg, rgba(59,130,246,0.2) 0%, rgba(37,99,235,0.2) 100%)',
              ],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'linear',
            }}
          />

          {/* Alternative border gradients - Uncomment the one you prefer */}

          {/* Option 2: Subtle gray theme */}

          <motion.div
            className="absolute inset-0 rounded-full"
            animate={{
              background: [
                'linear-gradient(0deg, rgba(75,85,99,0.1) 0%, rgba(107,114,128,0.1) 100%)',
                'linear-gradient(360deg, rgba(75,85,99,0.1) 0%, rgba(107,114,128,0.1) 100%)',
              ],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'linear',
            }}
          />

          {/* Option 3: Modern subtle theme */}

          <motion.div
            className="absolute inset-0 rounded-full"
            animate={{
              background: [
                'linear-gradient(0deg, rgba(255,255,255,0.1) 0%, rgba(209,213,219,0.1) 100%)',
                'linear-gradient(360deg, rgba(255,255,255,0.1) 0%, rgba(209,213,219,0.1) 100%)',
              ],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'linear',
            }}
          />

          {/* Option 4: No gradient, just border */}

          <motion.div className="absolute inset-0 rounded-full border-2 border-blue-500/20" />
        </motion.div>

        {/* Status indicator */}
        <motion.div
          className="absolute -bottom-0.5 -right-0.5 h-4 w-4"
          animate={{
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          <motion.div
            className="absolute inset-0 rounded-full bg-green-500"
            animate={{
              opacity: [1, 0.5, 1],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
          <div className="absolute inset-0 rounded-full bg-green-400" />
          <div className="absolute inset-0 rounded-full border-2 border-white dark:border-gray-900" />
        </motion.div>
      </motion.div>
    </Link>
  );
};
